import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { useContentId } from '@/components/SlugGate';
import { sanitizeHtml } from '@/lib/sanitize';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import ShareButtons from '@/components/ShareButtons';
import SEOHead from '@/components/SEOHead';
import { storyJsonLd } from '@/lib/jsonLd';
import { PremiumTeaser } from '@/components/PremiumLock';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  Moon,
  Sun,
  Type,
  X,
  Settings,
  Home,
  BookOpen,
  Heart,
  Clock,
  Crown,
  Sparkles,
} from 'lucide-react';

export default function Reader() {
  const id = useContentId();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [story, setStory] = useState(null);
  const [seriesNav, setSeriesNav] = useState(null);
  const [storyNav, setStoryNav] = useState({ previous: null, next: null });
  const [related, setRelated] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [progress, setProgress] = useState(0);
  const [initialProgress, setInitialProgress] = useState(0);
  const [settings, setSettings] = useState({
    nightMode: true,
    fontSize: 18,
    lineHeight: 2,
  });

  const lastSavedProgress = useRef(0);
  const saveTimeoutRef = useRef(null);
  const contentRef = useRef(null);

  // Fetch story and reading progress
  useEffect(() => {
    const fetchStory = async () => {
      try {
        // Reset scroll for new episode navigation
        window.scrollTo(0, 0);
        // Fetch story, progress, series-navigation and related in parallel
        const [storyRes, progressRes, navRes, neighborsRes, relatedRes, favRes] = await Promise.all([
          api.get(`/stories/${id}/read`),
          api.get(`/history/progress/${id}`).catch(() => ({ data: { progress: 0 } })),
          api.get(`/stories/${id}/series-navigation`).catch(() => ({ data: { in_series: false } })),
          api.get(`/stories/${id}/nav`).catch(() => ({ data: { previous: null, next: null } })),
          api.get(`/stories/${id}/related?limit=4`).catch(() => ({ data: [] })),
          user ? api.get(`/favorites/check/${id}`).catch(() => ({ data: { is_favorite: false } })) : Promise.resolve({ data: { is_favorite: false } }),
        ]);

        setStory(storyRes.data);
        setInitialProgress(progressRes.data.progress || 0);
        setSeriesNav(navRes.data?.in_series ? navRes.data : null);
        setStoryNav(neighborsRes.data || { previous: null, next: null });
        setRelated(relatedRes.data || []);
        setIsFavorite(!!favRes.data?.is_favorite);

        // Award points for reading (skip if this story is a locked teaser)
        if (!storyRes.data.is_locked) {
          try {
            await api.post('/points/earn/read_story?reference_id=' + id);
          } catch (e) { /* ignore duplicate */ }
        }
      } catch (error) {
        console.error('Error:', error);
        if (error.response?.status === 403) {
          navigate('/premium');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStory();

    return () => {
      // Save progress when leaving
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [id, navigate, user]);

  const toggleFavorite = async () => {
    if (!user) {
      toast.error('Inicia sessão para guardares favoritos');
      navigate('/login');
      return;
    }
    setFavLoading(true);
    try {
      if (isFavorite) {
        await api.delete(`/favorites/${id}`);
        setIsFavorite(false);
        toast.success('Removido dos favoritos');
      } else {
        await api.post(`/favorites/${id}`);
        setIsFavorite(true);
        toast.success('Adicionado aos favoritos');
      }
    } catch (error) {
      toast.error('Não foi possível atualizar os favoritos');
    } finally {
      setFavLoading(false);
    }
  };

  // Mark <html> as immersive while inside Reader so the platform badge
  // and other floating UI can be hidden via CSS on mobile.
  useEffect(() => {
    document.documentElement.setAttribute('data-reader', 'true');
    return () => {
      document.documentElement.removeAttribute('data-reader');
    };
  }, []);

  // Restore scroll position after content loads
  useEffect(() => {
    if (story && initialProgress > 0 && contentRef.current) {
      // Small delay to ensure content is rendered
      setTimeout(() => {
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        const targetScroll = (initialProgress / 100) * scrollHeight;
        window.scrollTo({ top: targetScroll, behavior: 'auto' });
      }, 100);
    }
  }, [story, initialProgress]);

  // Calculate and save progress on scroll
  const calculateProgress = useCallback(() => {
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollHeight <= 0) return 0;
    const currentProgress = Math.round((window.scrollY / scrollHeight) * 100);
    return Math.min(100, Math.max(0, currentProgress));
  }, []);

  const saveProgress = useCallback(async (progressValue) => {
    if (!user || progressValue === lastSavedProgress.current) return;
    
    // Only save if progress changed significantly (at least 5%)
    if (Math.abs(progressValue - lastSavedProgress.current) < 5 && progressValue < 95) return;
    
    try {
      await api.put(`/history/progress/${id}`, { progress: progressValue });
      lastSavedProgress.current = progressValue;
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  }, [id, user]);

  // Debounced progress save
  const debouncedSaveProgress = useCallback((progressValue) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveProgress(progressValue);
    }, 2000); // Save after 2 seconds of no scrolling
  }, [saveProgress]);

  // Handle scroll for controls and progress
  const handleScroll = useCallback(() => {
    setShowControls(false);
    const currentProgress = calculateProgress();
    setProgress(currentProgress);
    debouncedSaveProgress(currentProgress);
  }, [calculateProgress, debouncedSaveProgress]);

  useEffect(() => {
    let timeout;
    const showOnMove = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setShowControls(false), 3000);
    };

    window.addEventListener('mousemove', showOnMove);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('touchstart', showOnMove);

    // Initial progress calculation
    setProgress(calculateProgress());

    return () => {
      window.removeEventListener('mousemove', showOnMove);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchstart', showOnMove);
      clearTimeout(timeout);
      
      // Save final progress when leaving
      const finalProgress = calculateProgress();
      if (finalProgress !== lastSavedProgress.current) {
        saveProgress(finalProgress);
      }
    };
  }, [handleScroll, calculateProgress, saveProgress]);

  // Load settings from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('readerSettings');
    if (saved) {
      setSettings(JSON.parse(saved));
    }
  }, []);

  // Save settings
  const updateSettings = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('readerSettings', JSON.stringify(newSettings));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ruby"></div>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Conto não encontrado</p>
      </div>
    );
  }

  return (
    <div 
      className={`min-h-screen transition-colors duration-300 ${settings.nightMode ? 'bg-[#030303]' : 'bg-[#0a0a0a]'}`}
      data-testid="reader-page"
    >
      <SEOHead
        title={story.title}
        description={story.synopsis?.substring(0, 160) || `Lê "${story.title}" no Intimate.pt`}
        image={story.cover_image}
        jsonLd={storyJsonLd(story)}
      />
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          showControls ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'
        }`}
      >
        <div className="bg-gradient-to-b from-black/90 to-transparent backdrop-blur-sm px-3 sm:px-4 py-3 sm:py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                data-testid="reader-back-btn"
                className="text-white hover:bg-white/10 shrink-0 h-9 w-9 sm:h-10 sm:w-10"
              >
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
              </Button>
              <div className="min-w-0 flex-1">
                <h1 className="font-brand text-sm sm:text-lg text-white truncate">
                  {story.title}
                </h1>
                <p className="text-xs sm:text-sm text-gray-400 truncate">{story.author_name}</p>
              </div>
            </div>

            <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleFavorite}
                disabled={favLoading}
                data-testid="reader-fav-btn"
                className={`hover:bg-white/10 ${isFavorite ? 'text-ruby' : 'text-white'}`}
                aria-label={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                title={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
              >
                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-ruby' : ''}`} />
              </Button>
              <ShareButtons
                url={`${window.location.origin}/story/${story.slug || story.id}`}
                title={`${story.title} - Intimate.pt`}
                description={story.synopsis?.substring(0, 100) || `Lê "${story.title}" no Intimate.pt`}
                variant="popover"
                size="md"
                className="text-white hover:bg-white/10"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowSettings(!showSettings)}
                data-testid="reader-settings-btn"
                className="text-white hover:bg-white/10"
              >
                <Settings className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/home')}
                data-testid="reader-home-btn"
                className="text-white hover:bg-white/10"
              >
                <Home className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Settings Panel */}
      {showSettings && (
        <div className="fixed top-16 right-4 z-50 w-72 bg-surface rounded-lg border border-border shadow-xl p-4 animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Configurações</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowSettings(false)}
              className="h-8 w-8"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Night Mode */}
          <div className="flex items-center justify-between mb-6">
            <span className="text-sm text-gray-400">Modo Noturno</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => updateSettings('nightMode', !settings.nightMode)}
              data-testid="toggle-night-mode"
              className={`border-border ${settings.nightMode ? 'bg-ruby/20 border-ruby' : ''}`}
            >
              {settings.nightMode ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            </Button>
          </div>

          {/* Font Size */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Tamanho do Texto</span>
              <span className="text-sm text-white">{settings.fontSize}px</span>
            </div>
            <div className="flex items-center gap-3">
              <Type className="w-4 h-4 text-gray-500" />
              <Slider
                value={[settings.fontSize]}
                onValueChange={([value]) => updateSettings('fontSize', value)}
                min={14}
                max={28}
                step={1}
                className="flex-1"
                data-testid="font-size-slider"
              />
              <Type className="w-5 h-5 text-white" />
            </div>
          </div>

          {/* Line Height */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Espaçamento</span>
              <span className="text-sm text-white">{settings.lineHeight.toFixed(1)}</span>
            </div>
            <Slider
              value={[settings.lineHeight]}
              onValueChange={([value]) => updateSettings('lineHeight', value)}
              min={1.5}
              max={3}
              step={0.1}
              data-testid="line-height-slider"
            />
          </div>
        </div>
      )}

      {/* Content */}
      <main className="px-4 sm:px-6 py-20 sm:py-24">
        <article 
          className="max-w-[65ch] mx-auto"
          style={{
            fontSize: `${settings.fontSize}px`,
            lineHeight: settings.lineHeight
          }}
        >
          {/* Title */}
          <header className="text-center mb-8 sm:mb-12">
            {seriesNav && (
              <button
                onClick={() => navigate(`/series/${seriesNav.series_slug || seriesNav.series_id}`)}
                data-testid="reader-series-link"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ruby/90 hover:text-ruby mb-3 transition-colors"
              >
                <BookOpen className="w-3.5 h-3.5" />
                <span className="truncate max-w-[60vw]">{seriesNav.series_title}</span>
                <span className="opacity-60">·</span>
                <span>Capítulo {seriesNav.current_episode} de {seriesNav.total_episodes}</span>
              </button>
            )}
            <h1 className="font-brand text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 leading-tight">
              {story.title}
            </h1>
            <p className="text-muted-foreground">por {story.author_name}</p>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
              {story.reading_time ? (
                <Badge variant="outline" className="border-ruby/40 text-ruby-light bg-black/30" data-testid="reader-reading-time">
                  <Clock className="w-3 h-3 mr-1" /> ~{story.reading_time} min de leitura
                </Badge>
              ) : null}
              {story.category && (
                <Badge variant="outline" className="border-white/20 text-white/80 bg-black/30">
                  {story.category}
                </Badge>
              )}
              {story.is_premium && (
                <Badge className="bg-gold text-black">
                  <Crown className="w-3 h-3 mr-1" /> Premium
                </Badge>
              )}
              {initialProgress > 5 && initialProgress < 95 && (
                <Badge className="bg-ruby/20 border border-ruby/40 text-ruby-light" data-testid="reader-continue-chip">
                  A continuar de {initialProgress}%
                </Badge>
              )}
            </div>
            {seriesNav && (
              <div className="mt-5 max-w-md mx-auto">
                <div className="h-1 rounded-full bg-surface overflow-hidden" aria-hidden>
                  <div
                    className="h-full bg-ruby transition-all duration-500"
                    style={{ width: `${(seriesNav.current_episode / seriesNav.total_episodes) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </header>

          {/* Story Content */}
          {story.is_locked ? (
            <div ref={contentRef} data-testid="story-content">
              <PremiumTeaser
                teaserHtml={story.content}
                title="Continua a ler com Premium"
                message="Está a ver a prévia deste conto. Assine Premium para desbloquear a história completa — e toda a biblioteca Intimate.pt."
              />
            </div>
          ) : (
            <div 
              ref={contentRef}
              className={`font-reading reader-content ${settings.nightMode ? 'text-gray-300' : 'text-gray-200'}`}
              data-testid="story-content"
              style={{ fontSize: `${settings.fontSize}px`, lineHeight: '1.9', textAlign: 'justify' }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(story.content) }}
            />
          )}

          {/* End */}
          {!story.is_locked && (
            <div className="text-center mt-16 pt-8 border-t border-border">
              <p className="text-muted-foreground mb-6">— Fim do Capítulo —</p>

              {seriesNav ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
                    {seriesNav.previous ? (
                      <button
                        onClick={() => navigate(`/read/${seriesNav.previous.slug || seriesNav.previous.story_id}`)}
                        data-testid="reader-prev-episode"
                        className="group flex items-center gap-3 text-left p-4 rounded-xl border border-border bg-surface/50 hover:bg-surface hover:border-ruby/40 transition-all"
                      >
                        <ChevronLeft className="w-5 h-5 text-ruby shrink-0 group-hover:-translate-x-1 transition-transform" />
                        <div className="min-w-0">
                          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                            Capítulo {seriesNav.previous.episode_number} · Anterior
                          </div>
                          <div className="text-sm font-medium text-foreground truncate">
                            {seriesNav.previous.title}
                          </div>
                        </div>
                      </button>
                    ) : <div className="hidden sm:block" />}

                    {seriesNav.next ? (
                      <button
                        onClick={() => navigate(`/read/${seriesNav.next.slug || seriesNav.next.story_id}`)}
                        data-testid="reader-next-episode"
                        className="group flex items-center gap-3 text-right p-4 rounded-xl border border-ruby/30 bg-ruby/10 hover:bg-ruby/20 hover:border-ruby/60 transition-all sm:flex-row-reverse"
                      >
                        <ChevronRight className="w-5 h-5 text-ruby shrink-0 group-hover:translate-x-1 transition-transform" />
                        <div className="min-w-0">
                          <div className="text-xs uppercase tracking-wider text-ruby mb-1">
                            Capítulo {seriesNav.next.episode_number} · Seguinte
                          </div>
                          <div className="text-sm font-medium text-foreground truncate">
                            {seriesNav.next.title}
                          </div>
                        </div>
                      </button>
                    ) : (
                      <div className="flex items-center justify-center sm:justify-end gap-2 p-4 rounded-xl border border-border bg-surface/30 text-sm text-muted-foreground">
                        Chegou ao último capítulo
                      </div>
                    )}
                  </div>

                  <Button
                    variant="ghost"
                    onClick={() => navigate(`/series/${seriesNav.series_slug || seriesNav.series_id}`)}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid="reader-back-to-series"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Ver todos os capítulos
                  </Button>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Prev / Next standalone story */}
                  {(storyNav.previous || storyNav.next) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
                      {storyNav.previous ? (
                        <button
                          onClick={() => navigate(`/read/${storyNav.previous.slug || storyNav.previous.id}`)}
                          data-testid="reader-prev-story"
                          className="group flex items-center gap-3 text-left p-4 rounded-xl border border-border bg-surface/50 hover:bg-surface hover:border-ruby/40 transition-all"
                        >
                          <ChevronLeft className="w-5 h-5 text-ruby shrink-0 group-hover:-translate-x-1 transition-transform" />
                          <div className="min-w-0">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Conto anterior</div>
                            <div className="text-sm font-medium text-foreground truncate">{storyNav.previous.title}</div>
                          </div>
                        </button>
                      ) : <div className="hidden sm:block" />}

                      {storyNav.next ? (
                        <button
                          onClick={() => navigate(`/read/${storyNav.next.slug || storyNav.next.id}`)}
                          data-testid="reader-next-story"
                          className="group flex items-center gap-3 text-right p-4 rounded-xl border border-ruby/30 bg-ruby/10 hover:bg-ruby/20 hover:border-ruby/60 transition-all sm:flex-row-reverse"
                        >
                          <ChevronRight className="w-5 h-5 text-ruby shrink-0 group-hover:translate-x-1 transition-transform" />
                          <div className="min-w-0">
                            <div className="text-xs uppercase tracking-wider text-ruby mb-1">Conto seguinte</div>
                            <div className="text-sm font-medium text-foreground truncate">{storyNav.next.title}</div>
                          </div>
                        </button>
                      ) : <div className="hidden sm:block" />}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex flex-wrap justify-center gap-3">
                    <Button
                      onClick={toggleFavorite}
                      variant={isFavorite ? 'default' : 'outline'}
                      disabled={favLoading}
                      data-testid="reader-end-fav-btn"
                      className={isFavorite ? 'bg-ruby hover:bg-ruby-light' : 'border-border'}
                    >
                      <Heart className={`w-4 h-4 mr-2 ${isFavorite ? 'fill-white' : ''}`} />
                      {isFavorite ? 'Nos teus favoritos' : 'Adicionar aos favoritos'}
                    </Button>
                    <Button
                      onClick={() => navigate(`/story/${story?.slug || id}`)}
                      variant="outline"
                      data-testid="reader-back-to-story"
                      className="border-border"
                    >
                      Detalhes do conto
                    </Button>
                    <Button
                      onClick={() => navigate('/browse')}
                      data-testid="reader-explora"
                      className="bg-ruby hover:bg-ruby-light"
                    >
                      Explorar Mais
                    </Button>
                  </div>
                </div>
              )}

              {/* Premium incentive — free accounts only, end of story, before related content. */}
              {user && !user.is_premium && (
                <div
                  className="mt-10 relative rounded-2xl overflow-hidden border border-gold/30 bg-surface"
                  data-testid="reader-premium-cta"
                >
                  <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
                  <div className="relative z-10 p-6 md:p-8 text-center">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-gold/50 text-gold text-[11px] font-bold uppercase tracking-wider mb-3">
                      <Crown className="w-3 h-3" /> Premium
                    </span>
                    <h3 className="font-brand text-xl md:text-2xl font-bold text-foreground mb-2" data-testid="reader-premium-cta-title">
                      Há mais para descobrir.
                    </h3>
                    <p className="text-muted-foreground text-sm md:text-base max-w-xl mx-auto mb-5">
                      Gostaste deste conto? O Premium abre-te as portas a mais histórias, séries e conteúdos exclusivos.
                    </p>
                    <Button
                      onClick={() => navigate('/premium')}
                      data-testid="reader-premium-cta-btn"
                      className="bg-gold !text-black hover:bg-gold/90 rounded-full px-8 py-5 font-semibold shadow-lg active:scale-95 transition-transform"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Descobrir o Premium
                    </Button>
                  </div>
                </div>
              )}

              {/* Related stories */}
              {related.length > 0 && (
                <div className="mt-12 text-left" data-testid="reader-related-section">
                  <div className="flex items-end justify-between mb-4">
                    <h3 className="font-brand text-xl md:text-2xl font-bold text-foreground">
                      Talvez também gostes
                    </h3>
                    <Link to="/browse" className="text-sm text-ruby hover:underline flex items-center gap-1">
                      Ver mais <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {related.map((r) => (
                      <Link
                        key={r.id}
                        to={`/read/${r.slug || r.id}`}
                        className="group block"
                        data-testid={`reader-related-${r.id}`}
                      >
                        <div className="relative aspect-[3/4] rounded-lg overflow-hidden border border-border/50 group-hover:border-ruby/50 transition-all">
                          <img
                            src={r.cover_image || 'https://images.unsplash.com/photo-1518893063132-36e46dbe2428?w=400'}
                            alt={r.title}
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                          {r.is_premium && (
                            <Badge className="absolute top-2 right-2 bg-gold text-black text-[10px]">
                              <Crown className="w-3 h-3 mr-0.5" /> Premium
                            </Badge>
                          )}
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <p className="text-sm font-semibold text-white line-clamp-2 mb-1">{r.title}</p>
                            <div className="flex items-center gap-1 text-[11px] text-white/70">
                              <Clock className="w-3 h-3" />
                              {r.reading_time || 5} min
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </article>
      </main>

      {/* Progress Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50">
        <div className="h-1 bg-surface">
          <div 
            className="h-full bg-ruby transition-all duration-300"
            style={{ width: `${progress}%` }}
            data-testid="reading-progress-bar"
          />
        </div>
        {/* Progress indicator */}
        <div 
          className={`absolute right-4 bottom-3 text-xs font-medium transition-opacity duration-300 ${
            showControls ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <span className="px-2 py-1 rounded bg-surface/80 text-muted-foreground backdrop-blur-sm">
            {progress}%
          </span>
        </div>
      </div>
    </div>
  );
}
